<template>
  <div class="spinning-wheel-container" :class="{ 'jackpot-wheel-container': isJackpot }">
    <div class="wheel-container" :class="{ 'jackpot-wheel': isJackpot }">
      <!-- SVG-based wheel for better control and precision -->
      <svg
        class="wheel"
        ref="wheel"
        :style="wheelStyle"
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
      >
        <!-- Outer circle/border -->
        <circle cx="50" cy="50" r="50" :fill="isJackpot ? '#FFF9C4' : 'white'" />

        <!-- Jackpot outer ring (only for jackpot wheel) -->
        <circle v-if="isJackpot" cx="50" cy="50" r="50" fill="none" stroke="#FFD700" stroke-width="2" stroke-dasharray="3,1" class="jackpot-ring" />

        <!-- Wheel segments -->
        <g v-for="(segment, index) in segments" :key="index">
          <!-- Pie segment -->
          <path
            :d="getSegmentPath(index)"
            :fill="getSegmentColor(index)"
            stroke="white"
            :stroke-width="isJackpot ? 1 : 0.5"
            :class="{ 'jackpot-segment': isJackpot }"
          />

          <!-- Text path for curved text -->
          <path
            :id="`textPath-${index}`"
            :d="getTextPath(index)"
            fill="none"
          />

          <!-- Text element -->
          <text
            :fill="getTextColor(segment.color)"
            :font-size="getFontSize(segment.label)"
            font-weight="bold"
            text-anchor="middle"
            class="segment-text"
            :class="{ 'jackpot-text': isJackpot }"
            alignment-baseline="middle"
          >
            <textPath
              :href="`#textPath-${index}`"
              startOffset="50%"
              class="segment-text-path"
              lengthAdjust="spacingAndGlyphs"
              textLength="25"
            >
              {{ getFormattedLabel(segment.label) }}
            </textPath>
          </text>
        </g>

        <!-- Center circle -->
        <circle cx="50" cy="50" r="8" :fill="isJackpot ? '#FFD700' : 'white'" :stroke="isJackpot ? '#FFA000' : '#e0e0e0'" :stroke-width="isJackpot ? 2 : 1" />
        <circle cx="50" cy="50" r="2" :fill="isJackpot ? '#FFA000' : '#333'" />

        <!-- Jackpot star in center (only for jackpot wheel) -->
        <g v-if="isJackpot" class="jackpot-star">
          <polygon
            points="50,42 52,48 58,48 53,52 55,58 50,54 45,58 47,52 42,48 48,48"
            fill="#FFA000"
            stroke="#FFD700"
            stroke-width="0.5"
          />
        </g>
      </svg>

      <!-- Marker/pointer -->
      <div class="wheel-marker">
        <svg viewBox="0 0 30 30" width="100%" height="100%">
          <polygon points="15,0 0,30 30,30" :fill="isJackpot ? '#FFA000' : '#4CAF50'" />
        </svg>
      </div>

      <!-- Jackpot crown (only for jackpot wheel) -->
      <div v-if="isJackpot" class="jackpot-crown">
        <svg viewBox="0 0 50 30" width="100%" height="100%">
          <path d="M5,25 L10,10 L15,20 L25,5 L35,20 L40,10 L45,25 Z" fill="#FFD700" stroke="#FFA000" stroke-width="1" />
          <circle cx="10" cy="10" r="2" fill="#FF5252" />
          <circle cx="25" cy="5" r="2" fill="#2196F3" />
          <circle cx="40" cy="10" r="2" fill="#FF5252" />
        </svg>
      </div>
    </div>

    <div class="wheel-controls">
      <v-btn
        :color="isJackpot ? 'amber darken-2' : 'green'"
        large
        dark
        :loading="isSpinning"
        @click="spinWheel"
        :class="['spin-button', { 'jackpot-spin-button': isJackpot }]"
      >
        <!-- <v-icon left v-if="isJackpot">mdi-star-circle</v-icon> -->
        {{ isSpinning ? 'Spinning...' : (canSpin ? (isJackpot ? 'SPIN JACKPOT' : 'SPIN') : 'No Spins Left') }}
      </v-btn>
    </div>

    <!-- Result Dialog -->
    <v-dialog v-model="showResult" max-width="400">
      <v-card :class="{ 'jackpot-result-card': isJackpot && !isTryAgainPrize }">
        <v-card-title :class="['headline', { 'jackpot-result-title': isJackpot && !isTryAgainPrize }]">
          <v-icon v-if="isJackpot && !isTryAgainPrize" left color="amber darken-2" large>mdi-crown</v-icon>
          {{ isTryAgainPrize ? 'Better luck next time!' : (isJackpot ? 'JACKPOT WINNER!' : 'Congratulations!') }}
        </v-card-title>
        <v-card-text>
          <div class="result-content">
            <h2>{{ isTryAgainPrize ? 'You got:' : 'You won:' }}</h2>
            <div class="prize-display"
              :class="{
                'try-again-prize': isTryAgainPrize,
                'jackpot-prize-display': isJackpot && !isTryAgainPrize
              }">
              {{ currentPrize ? currentPrize.label : '' }}
            </div>
            <p v-if="currentPrize && currentPrize.description">{{ currentPrize.description }}</p>
            <div v-if="!isTryAgainPrize && currentPrize"
              :class="['prize-details', { 'jackpot-prize-details': isJackpot }]">
              <p v-if="currentPrize.type === 'discount'">
                <strong>Discount:</strong> {{ currentPrize.discountType === 'percentage' ? currentPrize.value + '%' : '$' + currentPrize.value }}
              </p>
              <p v-if="currentPrize.code">
                <strong>Code:</strong> {{ currentPrize.code }}
              </p>
            </div>
            <div v-if="isJackpot && !isTryAgainPrize" class="jackpot-confetti">
              <div v-for="n in 20" :key="n" class="confetti-piece" :style="getConfettiStyle(n)"></div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :color="isJackpot && !isTryAgainPrize ? 'amber darken-2' : 'primary'"
                 :dark="isJackpot && !isTryAgainPrize"
                 :text="!(isJackpot && !isTryAgainPrize)"
                 @click="closeResult">
            {{ isJackpot && !isTryAgainPrize ? 'Awesome!' : 'Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'SpinningWheel',
  props: {
    segments: {
      type: Array,
      required: true,
      default: () => []
    },
    // Optional custom colors for segments
    colors: {
      type: Array,
      default: () => ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8AC24A', '#FF5252']
    },
    // Whether the user can spin (has remaining spins)
    canSpin: {
      type: Boolean,
      default: true
    },
    // Whether this is a jackpot wheel
    isJackpot: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSpinning: false,
      rotation: 0,
      showResult: false,
      currentPrize: null,
      spinDuration: 5000, // 5 seconds
      spinRevolutions: 5, // Number of full rotations
      defaultColors: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8AC24A', '#FF5252']
    };
  },
  computed: {
    isTryAgainPrize() {
      if (!this.currentPrize) return false;

      // Get the prize label - handle both object and string formats
      const prizeLabel = typeof this.currentPrize === 'string'
        ? this.currentPrize
        : (this.currentPrize.label || '');

      // Get the prize description if available
      const prizeDescription = this.currentPrize.description || '';

      // Check if it's a try again prize by type, label, or description
      return this.currentPrize.type === 'try_again' ||
             prizeLabel.includes('Try Again') ||
             prizeDescription.includes('Try Again');
    },

    wheelStyle() {
      // Use a custom cubic-bezier curve for more realistic spinning with a slight wobble at the end
      // Always use the same transition timing for consistent spin speed
      const transitionTiming = this.isSpinning ?
        `transform ${this.spinDuration / 1000}s cubic-bezier(0.17, 0.67, 0.83, 0.97)` :
        'transform 0s';

      return {
        transform: `rotate(${this.rotation}deg)`,
        transition: transitionTiming,
        willChange: 'transform' // Optimize for animations
      };
    },
    segmentAngle() {
      return 360 / this.segments.length;
    }
  },
  methods: {
    // Generate SVG path for a pie segment
    getSegmentPath(index) {
      const angle = this.segmentAngle;
      // Start at top (90 degrees offset) and add a small offset to align with the marker
      const startAngle = index * angle - 90;
      const endAngle = startAngle + angle;

      // Convert angles to radians
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      // Calculate points on the circle
      const x1 = 50 + 48 * Math.cos(startRad);
      const y1 = 50 + 48 * Math.sin(startRad);
      const x2 = 50 + 48 * Math.cos(endRad);
      const y2 = 50 + 48 * Math.sin(endRad);

      // Create SVG path - move to center, line to first point, arc to second point, close path
      const largeArcFlag = angle > 180 ? 1 : 0;
      return `M 50 50 L ${x1} ${y1} A 48 48 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    },

    // Generate path for text placement
    getTextPath(index) {
      const angle = this.segmentAngle;
      const midAngle = (index * angle + angle / 2 - 90) * (Math.PI / 180);

      // Calculate radius for text placement (further inward from edge for more space)
      const radius = 30; // Reduced from 35 to move text inward

      // Calculate the middle angle of the segment for arc placement

      // Create a larger arc for text placement
      const arcLength = 30; // Increased from 20 to give more space for text
      const startAngle = midAngle - arcLength / radius / 2;
      const endAngle = midAngle + arcLength / radius / 2;

      const x1 = 50 + radius * Math.cos(startAngle);
      const y1 = 50 + radius * Math.sin(startAngle);
      const x2 = 50 + radius * Math.cos(endAngle);
      const y2 = 50 + radius * Math.sin(endAngle);

      return `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`;
    },

    // Get segment color
    getSegmentColor(index) {
      const colorIndex = index % this.colors.length;
      return this.colors[colorIndex] || this.defaultColors[colorIndex % this.defaultColors.length];
    },

    // Format label text to fit in the segment
    getFormattedLabel(label) {
      if (!label) return '';

      // Get the number of segments to determine how much space is available
      const segmentCount = this.segments.length;

      // Calculate max length based on segment count
      // More segments = less space per segment
      let maxLength = 14; // Increased from 12 to allow more text

      if (segmentCount > 8) {
        maxLength = 10; // Increased from 8
      } else if (segmentCount > 6) {
        maxLength = 12; // Increased from 10
      }

      // For mobile devices, use shorter text
      if (window.innerWidth < 480) {
        maxLength = Math.max(6, maxLength - 4); // Increased from 4 to 6
      }

      // Special handling for common patterns
      if (label.includes('%') && label.includes('Discount')) {
        // For percentage discount labels, ensure the percentage is visible
        const percentMatch = label.match(/(\d+)%/);
        if (percentMatch) {
          const percent = percentMatch[1];
          return `${percent}% OFF`; // Standardize to shorter form
        }
      } else if (label.includes('$') && label.includes('discount')) {
        // For fixed amount discount labels
        const amountMatch = label.match(/\$(\d+)/);
        if (amountMatch) {
          const amount = amountMatch[1];
          return `$${amount} OFF`; // Standardize to shorter form
        }
      }

      // Truncate and add ellipsis if needed
      if (label.length > maxLength) {
        // Try to find a space to break at
        const spaceIndex = label.lastIndexOf(' ', maxLength - 3);
        if (spaceIndex > maxLength / 2) {
          return label.substring(0, spaceIndex) + '...';
        }
        return label.substring(0, maxLength - 3) + '...';
      }

      return label;
    },

    // Get appropriate font size based on label length and segment count
    getFontSize(label) {
      if (!label) return 4;

      // Base font size on segment count and label length
      const segmentCount = this.segments.length;
      const labelLength = label.length;

      // Start with default size
      let fontSize = 4;

      // Adjust based on segment count
      if (segmentCount > 8) {
        fontSize = 3.5;
      } else if (segmentCount > 6) {
        fontSize = 3.8;
      }

      // Further adjust based on label length
      if (labelLength > 10) {
        fontSize -= 0.5;
      } else if (labelLength > 7) {
        fontSize -= 0.3;
      }

      // Ensure minimum size
      return Math.max(2.8, fontSize);
    },

    // Determine text color based on background color brightness
    getTextColor(backgroundColor) {
      // Default to white if no color is provided
      if (!backgroundColor) return 'white';

      // Extract RGB components
      let r, g, b;
      if (backgroundColor.startsWith('#')) {
        const hex = backgroundColor.substring(1);
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else if (backgroundColor.startsWith('rgb')) {
        const rgbMatch = backgroundColor.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
          [r, g, b] = rgbMatch.map(Number);
        } else {
          return 'white';
        }
      } else {
        return 'white';
      }

      // Calculate brightness using the YIQ formula
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // Return white for dark backgrounds, black for light backgrounds
      return brightness > 128 ? 'black' : 'white';
    },
    spinWheel() {
      if (this.isSpinning) return;

      // Close the result dialog if it's open
      if (this.showResult) {
        this.showResult = false;
      }

      // Make sure we're not in the middle of a transition
      // by waiting for the next tick before spinning
      this.$nextTick(() => {
        // Directly perform the spin
        this.performSpin();
      });
    },

    performSpin() {
      this.isSpinning = true;

      // Calculate the winning segment based on probabilities
      const winningSegment = this.getWinningSegment();

      // Store the winning segment index for debugging
      console.log('Winning segment index:', winningSegment);
      console.log('Winning segment:', this.segments[winningSegment]);

      // Calculate the final rotation
      const segmentAngle = 360 / this.segments.length;

      // Calculate base rotation (multiple full rotations)
      const baseRotation = this.spinRevolutions * 360;

      // Calculate the winning position
      // Add half a segment angle to land in the middle of the segment
      const winningRotation = (360 - (winningSegment * segmentAngle) - (segmentAngle / 2)) % 360;

      // Note: We don't need to normalize the current rotation as we're always adding to it

      // Calculate how much we need to rotate to reach the target position
      // We always want to rotate clockwise (add more rotation)
      const targetRotation = baseRotation + winningRotation;

      // Set the final rotation (current rotation + additional rotation needed)
      // This ensures we always spin in the same direction (clockwise)
      const finalRotation = this.rotation + targetRotation;
      this.rotation = finalRotation;

      // Add a subtle wobble effect at the end of the spin
      const wobbleTime = 500; // 500ms for the wobble effect

      // Store the winning segment data before the spin completes
      // This ensures we use the same segment data throughout the process
      const winningSegmentData = JSON.parse(JSON.stringify(this.segments[winningSegment]));

      // Log the raw segment data for debugging
      console.log('Raw winning segment data:', winningSegmentData);

      // Get the segment label - handle both object and string formats
      const segmentLabel = typeof winningSegmentData === 'string'
        ? winningSegmentData
        : (winningSegmentData.label || '');

      console.log('Segment label:', segmentLabel);

      // Set the type based on the label if not already set
      if (segmentLabel.includes('Try Again')) {
        winningSegmentData.type = 'try_again';
        winningSegmentData.label = segmentLabel;
      } else if (segmentLabel.includes('OFF')) {
        winningSegmentData.type = 'discount';
        winningSegmentData.label = segmentLabel;

        // Determine if it's a percentage or fixed discount
        if (segmentLabel.includes('%')) {
          winningSegmentData.discountType = 'percentage';
          // Extract the percentage value
          const percentMatch = segmentLabel.match(/(\d+)%/);
          if (percentMatch) {
            winningSegmentData.value = parseInt(percentMatch[1], 10);
          }
        } else if (segmentLabel.includes('$')) {
          winningSegmentData.discountType = 'fixed';
          // Extract the dollar value
          const dollarMatch = segmentLabel.match(/\$(\d+)/);
          if (dollarMatch) {
            winningSegmentData.value = parseInt(dollarMatch[1], 10);
          }
        }
      }

      console.log('Processed winning segment data:', winningSegmentData);

      // Show result after spin completes (including wobble time)
      setTimeout(() => {
        this.isSpinning = false;
        console.log('Final rotation:', winningSegment);

        // Set the current prize to the winning segment data
        this.currentPrize = winningSegmentData;

        // Log the actual prize for debugging
        console.log('Final prize:', this.currentPrize);

        // Small delay before showing the result for better UX
        setTimeout(() => {
          this.showResult = true;
          // Emit event with the prize and segment index
          this.$emit('prize-won', this.currentPrize, winningSegment);

          // Note: We don't reset the wheel here because we want to keep the visual state
          // until the user closes the result dialog. The wheel will be reset in closeResult().
        }, 300);
      }, this.spinDuration + wobbleTime);
    },
    getWinningSegment() {
      // Check if segments have probabilities
      const hasProbabilities = this.segments.some(segment => segment.probability !== undefined);

      // Store the selected segment index
      let selectedIndex;

      if (hasProbabilities) {
        // Use weighted random selection based on probabilities
        const random = Math.random();
        let cumulativeProbability = 0;

        for (let i = 0; i < this.segments.length; i++) {
          // Use the segment's probability or equal distribution if not specified
          const probability = this.segments[i].probability !== undefined
            ? this.segments[i].probability
            : 1 / this.segments.length;

          cumulativeProbability += probability;

          if (random <= cumulativeProbability) {
            selectedIndex = i;
            break;
          }
        }

        // Fallback to random selection if probabilities don't add up to 1
        if (selectedIndex === undefined) {
          selectedIndex = Math.floor(Math.random() * this.segments.length);
        }
      } else {
        // Simple random selection if no probabilities are defined
        selectedIndex = Math.floor(Math.random() * this.segments.length);
      }

      // Log the selected segment for debugging
      console.log('Selected segment index:', selectedIndex);
      console.log('Selected segment:', this.segments[selectedIndex]);

      return selectedIndex;
    },
    closeResult() {
      this.showResult = false;
      // Reset the wheel after closing the result dialog
      this.resetWheel();
    },

    resetWheel() {
      // Reset the wheel to its initial state
      // This ensures the wheel is ready for a fresh spin next time
      this.currentPrize = null;

      // Reset the rotation to 0 (or a multiple of 360) to avoid accumulating large rotation values
      // We use Math.floor to ensure we keep full rotations and avoid visual jumps
      this.rotation = Math.floor(this.rotation / 360) * 360;

      console.log('Wheel reset, new rotation:', this.rotation);
    },

    // Generate random confetti style for jackpot celebration
    getConfettiStyle(index) {
      const colors = ['#FFD700', '#FFA000', '#FF5252', '#2196F3', '#4CAF50', '#9C27B0', '#FF9800'];
      const size = Math.floor(Math.random() * 10) + 5; // 5-15px
      const left = Math.floor(Math.random() * 100); // 0-100%
      const animationDelay = Math.random() * 3; // 0-3s delay
      const animationDuration = (Math.random() * 2) + 2; // 2-4s duration

      return {
        backgroundColor: colors[index % colors.length],
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        animationDelay: `${animationDelay}s`,
        animationDuration: `${animationDuration}s`
      };
    }
  }
};
</script>

<style scoped>
.spinning-wheel-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 100%;
}

/* Jackpot wheel container styles */
.jackpot-wheel-container {
  padding: 30px 20px;
  background: radial-gradient(circle, rgba(255,249,196,0.3) 0%, rgba(255,255,255,0) 70%);
}

.segment-text {
  font-family: Arial, sans-serif;
  font-weight: bold;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.1);
  stroke-width: 0.5px;
}

.jackpot-text {
  font-weight: 900;
  stroke: rgba(255, 193, 7, 0.3);
  stroke-width: 0.8px;
}

.segment-text-path {
  letter-spacing: 0.2px;
  dominant-baseline: middle;
  text-rendering: optimizeLegibility;
}

.wheel-container {
  position: relative;
  width: 400px;
  height: 400px;
  margin: 0 auto 30px;
  max-width: 100%;
  filter: drop-shadow(0px 5px 15px rgba(0, 0, 0, 0.1));
  padding-top: 0px; /* Removed extra top padding */
}

/* Jackpot wheel styles */
.jackpot-wheel {
  filter: drop-shadow(0px 5px 15px rgba(255, 193, 7, 0.3));
}

.wheel {
  position: relative;
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 10px rgba(0, 0, 0, 0.15));
  will-change: transform;
}

text {
  font-family: Arial, sans-serif;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.3);
  dominant-baseline: middle;
}

.wheel-marker {
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  width: 30px;
  height: 30px;
  z-index: 2;
  filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.3));
  pointer-events: none;
}

.jackpot-crown {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 40px;
  z-index: 3;
  filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.3));
  animation: float 3s ease-in-out infinite;
}

.jackpot-ring {
  animation: rotate 30s linear infinite;
}

.jackpot-segment {
  filter: brightness(1.05);
}

.jackpot-star {
  animation: pulse-star 2s ease-in-out infinite alternate;
}

.wheel-controls {
  margin-top: 30px;
  text-align: center;
}

.spin-button {
  min-width: 180px;
  font-size: 20px;
  font-weight: bold;
  padding: 12px 24px !important;
  border-radius: 30px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 10px rgba(76, 175, 80, 0.5), 0 0 20px rgba(76, 175, 80, 0.3) !important;
  letter-spacing: 1px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: glow 1.5s ease-in-out infinite alternate;
}

.jackpot-spin-button {
  min-width: 200px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 10px rgba(255, 193, 7, 0.5), 0 0 20px rgba(255, 193, 7, 0.3) !important;
  animation: jackpot-glow 1.5s ease-in-out infinite alternate;
}

.spin-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15), 0 0 15px rgba(76, 175, 80, 0.8), 0 0 30px rgba(76, 175, 80, 0.5) !important;
  animation-play-state: paused;
}

.jackpot-spin-button:hover:not(:disabled) {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15), 0 0 15px rgba(255, 193, 7, 0.8), 0 0 30px rgba(255, 193, 7, 0.5) !important;
}

.spin-button:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 10px rgba(76, 175, 80, 0.5), 0 0 20px rgba(76, 175, 80, 0.3) !important;
  animation-play-state: paused;
}

.jackpot-spin-button:active:not(:disabled) {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 10px rgba(255, 193, 7, 0.5), 0 0 20px rgba(255, 193, 7, 0.3) !important;
}

@media (max-width: 480px) {
  .wheel-container {
    width: 300px;
    height: 300px;
  }

  .wheel-marker {
    width: 25px;
    height: 25px;
    top: -16px; /* Adjusted for mobile view */
  }

  .jackpot-crown {
    top: -25px;
    width: 50px;
    height: 30px;
  }

  .segment-text {
    font-size: 3.2;
    stroke-width: 0.3px;
  }

  .segment-text-path {
    letter-spacing: 0;
  }

  .spin-button {
    min-width: 150px;
    font-size: 18px;
    padding: 10px 20px !important;
  }

  .jackpot-spin-button {
    min-width: 180px;
  }
}

.result-content {
  text-align: center;
  padding: 20px 0;
  position: relative;
  overflow: hidden;
}

.prize-display {
  font-size: 24px;
  font-weight: bold;
  margin: 5px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #4CAF50;
}

.jackpot-prize-display {
  color: #FFA000;
  background-color: #FFF9C4;
  border: 2px solid #FFD700;
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.5);
  font-size: 28px;
  padding: 20px;
  animation: pulse-prize 2s ease-in-out infinite alternate;
}

.try-again-prize {
  color: #f44336; /* Red color for Try Again prizes */
  background-color: #ffebee; /* Light red background */
}

.prize-details {
  margin-top: 15px;
  padding: 10px;
  background-color: #e8f5e9;
  border-radius: 4px;
  font-size: 14px;
  text-align: left;
}

.jackpot-prize-details {
  background-color: #FFF8E1;
  border: 1px solid #FFE082;
  padding: 15px;
}

.prize-details p {
  margin: 5px 0;
}

.jackpot-result-card {
  background: linear-gradient(135deg, #FFF9C4 0%, #FFFFFF 100%);
  border: 2px solid #FFD700;
  overflow: hidden;
}

.jackpot-result-title {
  background-color: #FFF9C4;
  color: #FFA000;
  font-weight: bold;
  letter-spacing: 1px;
}

.jackpot-confetti {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.confetti-piece {
  position: absolute;
  top: -20px;
  border-radius: 50%;
  animation: fall 4s ease-in infinite;
}

@keyframes glow {
  from {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 10px rgba(76, 175, 80, 0.5), 0 0 20px rgba(76, 175, 80, 0.3);
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
  }
  to {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 15px rgba(76, 175, 80, 0.8), 0 0 30px rgba(76, 175, 80, 0.5);
    text-shadow: 0 0 10px rgba(255, 255, 255, 1);
  }
}

@keyframes jackpot-glow {
  from {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 10px rgba(255, 193, 7, 0.5), 0 0 20px rgba(255, 193, 7, 0.3);
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
  }
  to {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 15px rgba(255, 193, 7, 0.8), 0 0 30px rgba(255, 193, 7, 0.5);
    text-shadow: 0 0 10px rgba(255, 255, 255, 1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(-5px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse-star {
  from {
    transform: scale(1);
    opacity: 0.8;
  }
  to {
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes pulse-prize {
  from {
    box-shadow: 0 0 5px rgba(255, 193, 7, 0.3);
    transform: scale(1);
  }
  to {
    box-shadow: 0 0 15px rgba(255, 193, 7, 0.7);
    transform: scale(1.03);
  }
}

@keyframes fall {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: translateY(650px) rotate(360deg);
    opacity: 0;
  }
}
</style>
